import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import ActionBtns from './ActionBtns';
import FormModal from './FormModal';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
// ─────────────────────────────────────────────────────────────────────────────
// 2. SUB CATEGORIES PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const SubCategoriesPage = ({ companyId }) => {
  const crud = useCRUD(subCategoryController, companyId);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [catList, setCatList]   = useState([]);
  const [form, setForm]         = useState({ name:'',code:'',categoryId:'',categoryName:'',description:'',sortOrder:0,status:'Active' });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const [errors, setErrors] = useState([]);

  useEffect(() => { categoryController.getList(companyId).then(r => r.success && setCatList(r.data)); }, [companyId]);

  const openAdd  = () => { setEditItem(null); setForm({ name:'',code:'',categoryId:'',categoryName:'',description:'',sortOrder:0,status:'Active' }); setErrors([]); setShowForm(true); };
  const openEdit = (r) => { setEditItem(r); setForm({...r}); setErrors([]); setShowForm(true); };

  const handleSave = async () => {
    setSaving(true); setErrors([]);
    const cat = catList.find(c => c.id === form.categoryId || c._id === form.categoryId);
    const payload = { ...form, categoryName: cat?.name || form.categoryName, companyId };
    const res = editItem ? await subCategoryController.update(editItem.id, payload) : await subCategoryController.create(payload);
    setSaving(false);
    if (res.success) { crud.show(res.message); setShowForm(false); crud.fetchAll(); }
    else setErrors(res.errors?.length ? res.errors : [res.message]);
  };

  const columns = [
    { label:'Code', key:'code', render: v => <span style={{ fontFamily:'monospace', fontSize:12, color:C.purple }}>{v||'—'}</span> },
    { label:'Sub Category', key:'name', render: v => <div style={{ fontWeight:700 }}>{v}</div> },
    { label:'Parent Category', key:'categoryName', render: v => <Badge color={C.primary} bg={`${C.primary}15`}>{v||'—'}</Badge> },
    { label:'Description', key:'description', render: v => <div style={{ maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:12, color:C.textSoft }}>{v||'—'}</div> },
    { label:'Status', key:'status', render: v => <StatusBadge status={v}/> },
    { label:'', key:'actions', render:(_, r) => <ActionBtns onEdit={()=>openEdit(r)} onDelete={()=>crud.handleDelete(r.id, `"${r.name}"`)} /> },
  ];

  return (
    <div style={S.page}>
      {crud.ToastEl}{crud.Dialog}
      <PageHeader title="Sub Categories" subtitle="Manage product sub-categories" Icon={Layers} color={C.purple}
        action={<button style={S.btnPri} onClick={openAdd}><Plus size={15}/> Add Sub Category</button>} />
      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}>
          <FilterBar search={crud.search} onSearch={crud.setSearch}
            filters={[
              { key:'categoryId', value:crud.filters.categoryId||'', onChange:v=>crud.setFilters(p=>({...p,categoryId:v})),
                options:[{value:'',label:'All Categories'},...catList.map(c=>({value:c.id||c._id,label:c.name}))] },
              { key:'status', value:crud.filters.status||'All', onChange:v=>crud.setFilters(p=>({...p,status:v==='All'?'':v})),
                options:[{value:'All',label:'All Status'},{value:'Active',label:'Active'},{value:'Inactive',label:'Inactive'}] },
            ]} />
        </div>
        <DataTable columns={columns} data={crud.data} loading={crud.loading} emptyTitle="No sub-categories found"
          page={crud.page} totalPages={crud.totalPages} onPageChange={crud.setPage} total={crud.total} />
      </div>

      {showForm && (
        <FormModal title={editItem?'Edit Sub Category':'Add Sub Category'} onClose={()=>setShowForm(false)} onSave={handleSave} saving={saving}>
          <ErrList errors={errors} />
          <FormGrid>
            <FormField label="Sub Category Name *"><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} /></FormField>
            <FormField label="Code"><input style={S.input} value={form.code} onChange={e=>set('code',e.target.value)} /></FormField>
            <FormField label="Parent Category *">
              <select style={S.select} value={form.categoryId} onChange={e=>set('categoryId',e.target.value)}>
                <option value="">Select Category</option>
                {catList.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.name}</option>)}
              </select>
            </FormField>
            <FormField label="Sort Order"><input type="number" style={S.input} value={form.sortOrder} onChange={e=>set('sortOrder',e.target.value)} /></FormField>
            <FormField label="Status"><select style={S.select} value={form.status} onChange={e=>set('status',e.target.value)}><option>Active</option><option>Inactive</option></select></FormField>
            <FormField label="Description" col={2}><textarea style={S.textarea} value={form.description} onChange={e=>set('description',e.target.value)} /></FormField>
          </FormGrid>
        </FormModal>
      )}
    </div>
  );
};
export default SubCategoriesPage;